
import React, { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";

import EnglishQuizForm from "./components/EnglishQuizForm";

import {
  useGetEnglishQuizByIdQuery,
  useUpdateEnglishQuizMutation,
} from "../../slices/englishQuizApiSlice";

/**
 * EnglishQuizEdit
 *
 * Page for editing an existing English quiz.
 *
 * Responsibilities:
 * - Load quiz details
 * - Populate EnglishQuizForm
 * - Update quiz using RTK Query
 * - Handle loading and API errors
 * - Navigate after successful update
 */
const EnglishQuizEdit = () => {
  const navigate = useNavigate();
  const { quizId } = useParams();

  // ======================================================
  // Load Quiz
  // ======================================================

  const {
    data,
    isLoading: isQuizLoading,
    isFetching: isQuizFetching,
    isError: isQuizError,
    error: quizError,
  } = useGetEnglishQuizByIdQuery(quizId, {
    skip: !quizId,
  });

  // ======================================================
  // Update Quiz
  // ======================================================

  const [
    updateEnglishQuiz,
    {
      isLoading: isUpdating,
      error: updateError,
      isSuccess: isUpdateSuccess,
    },
  ] = useUpdateEnglishQuizMutation();

  // ======================================================
  // Extract Quiz
  // ======================================================

  const quiz = useMemo(() => {
    return (
      data?.quiz ||
      data?.data ||
      data
    );
  }, [data]);

  // ======================================================
  // Navigate After Update
  // ======================================================

  useEffect(() => {
    if (!isUpdateSuccess) {
      return;
    }

    navigate(
      `/englishquiz/${quizId}`,
      {
        replace: true,
      },
    );
  }, [
    isUpdateSuccess,
    navigate,
    quizId,
  ]);

  // ======================================================
  // Submit
  // ======================================================

  const handleSubmit = async (formData) => {
    if (!quizId || isUpdating) {
      return;
    }

    try {
      await updateEnglishQuiz({
        id: quizId,
        quizId,
        data: formData,
      }).unwrap();
    } catch (error) {
      // The mutation error is exposed through updateError.
      console.error(
        "Failed to update English quiz:",
        error,
      );
    }
  };

  // ======================================================
  // Cancel
  // ======================================================

  const handleCancel = () => {
    navigate(
      quizId
        ? `/englishquiz/${quizId}`
        : "/englishquiz",
    );
  };

  // ======================================================
  // Error Helpers
  // ======================================================

  const getErrorMessage = (error) => {
    if (!error) {
      return null;
    }

    if (typeof error === "string") {
      return error;
    }

    return (
      error?.data?.message ||
      error?.error ||
      error?.message ||
      "Something went wrong. Please try again."
    );
  };

  const quizErrorMessage =
    getErrorMessage(quizError);

  const updateErrorMessage =
    getErrorMessage(updateError);

  // ======================================================
  // Loading
  // ======================================================

  if (
    isQuizLoading ||
    isQuizFetching
  ) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="border-b border-slate-200 bg-white">
          <div className="mx-auto max-w-7xl px-4 py-5 sm:px-6 lg:px-8">
            <div className="h-4 w-32 animate-pulse rounded bg-slate-200" />

            <div className="mt-3 h-8 w-72 animate-pulse rounded bg-slate-200" />

            <div className="mt-2 h-4 w-96 max-w-full animate-pulse rounded bg-slate-200" />
          </div>
        </div>

        <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="space-y-6">
              <div className="h-6 w-48 animate-pulse rounded bg-slate-200" />

              <div className="grid gap-5 md:grid-cols-2">
                <div className="h-12 animate-pulse rounded-lg bg-slate-100" />
                <div className="h-12 animate-pulse rounded-lg bg-slate-100" />
                <div className="h-12 animate-pulse rounded-lg bg-slate-100" />
                <div className="h-12 animate-pulse rounded-lg bg-slate-100" />
              </div>

              <div className="h-32 animate-pulse rounded-xl bg-slate-100" />
            </div>
          </div>
        </main>
      </div>
    );
  }

  // ======================================================
  // Quiz Load Error
  // ======================================================

  if (
    isQuizError ||
    !quiz
  ) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="mx-auto flex min-h-screen max-w-2xl items-center justify-center px-4">
          <div className="w-full rounded-2xl border border-red-200 bg-white p-6 text-center shadow-sm sm:p-8">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100 text-red-600">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="9"
                />

                <path
                  strokeLinecap="round"
                  d="M12 8v5"
                />

                <path
                  strokeLinecap="round"
                  d="M12 16h.01"
                />
              </svg>
            </div>

            <h1 className="mt-4 text-xl font-semibold text-slate-900">
              Unable to load English quiz
            </h1>

            <p className="mt-2 text-sm leading-6 text-slate-500">
              {quizErrorMessage ||
                "The requested quiz could not be found."}
            </p>

            <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row">
              <button
                type="button"
                onClick={() =>
                  navigate("/englishquiz")
                }
                className="inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                Back to English Quizzes
              </button>

              <button
                type="button"
                onClick={() =>
                  window.location.reload()
                }
                className="inline-flex items-center justify-center rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ======================================================
  // Render
  // ======================================================

  return (
    <div className="min-h-screen bg-slate-50">
      {/* ==================================================
          Header
      ================================================== */}

      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-5 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2 text-sm text-slate-500">
                <button
                  type="button"
                  onClick={() =>
                    navigate("/englishquiz")
                  }
                  className="transition hover:text-indigo-600"
                >
                  English Quizzes
                </button>

                <span>/</span>

                <button
                  type="button"
                  onClick={() =>
                    navigate(
                      `/englishquiz/${quizId}`,
                    )
                  }
                  className="max-w-xs truncate transition hover:text-indigo-600"
                >
                  {quiz?.name ||
                    "English Quiz"}
                </button>

                <span>/</span>

                <span className="text-slate-700">
                  Edit
                </span>
              </div>

              <h1 className="mt-2 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                Edit English Quiz
              </h1>

              <p className="mt-1 max-w-2xl text-sm text-slate-500">
                Update the quiz configuration and
                settings.
              </p>
            </div>

            <button
              type="button"
              onClick={handleCancel}
              disabled={isUpdating}
              className="inline-flex shrink-0 items-center justify-center rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>

      {/* ==================================================
          Content
      ================================================== */}

      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        {/* Update Error */}

        {updateErrorMessage && (
          <div
            role="alert"
            className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4"
          >
            <div className="flex gap-3">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="9"
                  />

                  <path
                    strokeLinecap="round"
                    d="M12 8v5"
                  />

                  <path
                    strokeLinecap="round"
                    d="M12 16h.01"
                  />
                </svg>
              </div>

              <div>
                <p className="text-sm font-semibold text-red-900">
                  Unable to update English quiz
                </p>

                <p className="mt-1 text-sm text-red-700">
                  {updateErrorMessage}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ==================================================
            Form
        ================================================== */}

        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <EnglishQuizForm
            mode="edit"
            initialData={quiz}
            quiz={quiz}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isLoading={isUpdating}
            error={updateError}
          />
        </div>
      </main>
    </div>
  );
};

export default EnglishQuizEdit;

